import React, { useCallback, useMemo, useRef } from 'react';
import {
  useRecoilState,
  useRecoilValue,
  useSetRecoilState,
} from 'recoil';
import { userInformationAtom } from '../../../../Services/_states';
import {
  interveneChatCountsAtom,
  interveneFilterAtom,
  interveneMessageListAtom,
  interveneUserDetailAtom,
  interveneUserListAtom,
} from '../../state/interveneAtoms';
import { createHandleChatMessage } from '../handlers/handleChatMessage';
import { createHandleCountsUpdated } from '../handlers/handleCountsUpdated';
import { createHandleListUpdated, createHandleSessionUpdated } from '../handlers/handleListUpdated';
import { createHandleMessageStatus } from '../handlers/handleMessageStatus';
import { createHandlePresenceUpdated } from '../handlers/handlePresenceUpdated';
import { useInterveneChatSocket } from '../hooks/useInterveneChatSocket';
import { useInterveneOrgNotificationsBridge } from '../hooks/useInterveneOrgNotificationsBridge';
import { InterveneSocketContextProvider } from '../hooks/useInterveneSocketContext';
import { useInterveneStatusSocket } from '../hooks/useInterveneStatusSocket';
import { createInterveneSocketRouter } from '../router/interveneSocketRouter';
import { intervenePresenceAtom, interveneSocketConnectionAtom } from '../state/socketAtoms';
import { getInterveneCompanyId } from '../../utils/interveneOrganisationScope';

export function InterveneSocketProvider({ children }) {
  const userInfo = useRecoilValue(userInformationAtom);
  const [globalFilters] = useRecoilState(interveneFilterAtom);
  const [activeUser, setActiveUser] = useRecoilState(interveneUserDetailAtom);
  const setUserList = useSetRecoilState(interveneUserListAtom);
  const setMessageList = useSetRecoilState(interveneMessageListAtom);
  const setChatCounts = useSetRecoilState(interveneChatCountsAtom);
  const setPresence = useSetRecoilState(intervenePresenceAtom);
  const setSocketConnection = useSetRecoilState(interveneSocketConnectionAtom);

  const organisationId = getInterveneCompanyId(globalFilters, userInfo);
  const botNumber = globalFilters?.botNumber || activeUser?.bot_number || '';
  const phoneNumber = activeUser?.phone_number || '';
  const agentName = userInfo?.username || userInfo?.first_name || 'Agent';
  const currentUserId = userInfo?.id ?? null;

  const activeUserRef = useRef(activeUser);
  const filtersRef = useRef(globalFilters);
  const currentUserIdRef = useRef(currentUserId);
  activeUserRef.current = activeUser;
  filtersRef.current = globalFilters;
  currentUserIdRef.current = currentUserId;

  const router = useMemo(
    () =>
      createInterveneSocketRouter({
        onChatMessage: createHandleChatMessage({
          getActiveUser: () => activeUserRef.current,
          botNumber: () => filtersRef.current?.botNumber || activeUserRef.current?.bot_number || botNumber,
          setMessageList,
        }),
        onSessionUpdated: createHandleSessionUpdated({
          getActiveUser: () => activeUserRef.current,
          getFilters: () => filtersRef.current,
          getCurrentUserId: () => currentUserIdRef.current,
          setUserList,
          setActiveUser,
        }),
        onListUpdated: createHandleListUpdated({
          getActiveUser: () => activeUserRef.current,
          getFilters: () => filtersRef.current,
          getCurrentUserId: () => currentUserIdRef.current,
          setUserList,
          setActiveUser,
        }),
        onPresenceUpdated: createHandlePresenceUpdated({
          agentName,
          setPresence,
        }),
        onCountsUpdated: createHandleCountsUpdated({
          setChatCounts,
          getBotNumber: () => filtersRef.current?.botNumber,
        }),
        onMessageStatus: createHandleMessageStatus({ setMessageList }),
      }),
    [agentName, botNumber, currentUserId, setActiveUser, setChatCounts, setMessageList, setPresence, setUserList]
  );

  const routePayload = useCallback(
    (payload) => {
      router(payload);
    },
    [router]
  );

  useInterveneOrgNotificationsBridge(routePayload);

  const handleChatStatus = useCallback(
    (status) => {
      setSocketConnection((prev) => ({ ...prev, chat: status }));
    },
    [setSocketConnection]
  );

  const handleStatusSocketStatus = useCallback(
    (status) => {
      setSocketConnection((prev) => ({ ...prev, status }));
    },
    [setSocketConnection]
  );

  const { sendMarkRead } = useInterveneChatSocket({
    organisationId,
    botNumber,
    phoneNumber,
    agentName,
    onPayload: routePayload,
    onStatusChange: handleChatStatus,
  });

  useInterveneStatusSocket({
    botNumber,
    phoneNumber,
    onPayload: routePayload,
    onStatusChange: handleStatusSocketStatus,
  });

  const presenceKey =
    botNumber && phoneNumber ? `${botNumber}:${phoneNumber}` : '';
  const presenceViewers = useRecoilValue(intervenePresenceAtom);
  const viewers = presenceKey ? presenceViewers[presenceKey] || [] : [];

  const contextValue = useMemo(
    () => ({
      sendMarkRead,
      viewers,
      organisationId,
    }),
    [sendMarkRead, viewers, organisationId]
  );

  return (
    <InterveneSocketContextProvider value={contextValue}>
      {children}
    </InterveneSocketContextProvider>
  );
}

export { useInterveneSocketContext } from '../hooks/useInterveneSocketContext';
